/**
 * Build Blueprint: copy a small voxel model onto your own plot. Pure and deterministic; the
 * browser and the server run the same comparison.
 *
 * Coordinates (editor, preview and validator alike): x runs east (left → right in the layer
 * editor), z runs south (top → bottom in the layer editor), y is the layer, 0 on the ground.
 * Plots are cubes of `size` (at most MAX_PLOT). A build matches when every cell holds exactly the
 * same block as the blueprint; levels that accept rotation also accept the blueprint turned a
 * quarter, half or three-quarter turn about the vertical axis through the plot's centre.
 */

export const MAX_PLOT = 12;
/** Gold medal: match the blueprint with at most this many wrong checks before it. */
export const GOLD_MAX_WRONG_CHECKS = 2;

/** Original, generic block styles (website colours, no game textures). */
export const BLOCKS = [
  'stone',
  'cobble',
  'planks',
  'log',
  'brick',
  'glass',
  'leaves',
  'sand',
  'slate',
  'lantern',
  'wool-red',
  'wool-blue',
] as const;
export type BlockId = (typeof BLOCKS)[number];

/** One block: x, y, z, block. */
export type Voxel = [x: number, y: number, z: number, block: BlockId];

export interface BlueprintLevel {
  id: string;
  version: number;
  title: string;
  tier: 1 | 2 | 3;
  goal: string;
  size: number;
  /** Blocks the player may use. */
  palette: BlockId[];
  /** Whether the blueprint may be built turned about the vertical axis. */
  rotations: boolean;
  /** The blueprint, which is also the known solution. */
  model: Voxel[];
}

export interface Mismatch {
  x: number;
  y: number;
  z: number;
  kind: 'missing' | 'extra' | 'wrong';
  want: BlockId | null;
  got: BlockId | null;
}

export interface CompareResult {
  match: boolean;
  /** Quarter turns of the blueprint the build was compared against (the closest one). */
  rotation: 0 | 1 | 2 | 3;
  mismatches: Mismatch[];
}

export const cellKey = (x: number, y: number, z: number) => `${x},${y},${z}`;

/** Turns (x, z) a quarter turn clockwise, seen from above, `turns` times inside a plot. */
export function rotate(v: Voxel, turns: number, size: number): Voxel {
  let [x, y, z, b] = v;
  for (let i = 0; i < turns % 4; i++) [x, z] = [size - 1 - z, x];
  return [x, y, z, b];
}

/** Checks bounds, block ids and duplicates. Returns a map "x,y,z" → block, or an error. */
export function toCells(
  voxels: readonly Voxel[],
  size: number,
  palette: readonly BlockId[] = BLOCKS,
): Map<string, BlockId> | { error: string } {
  if (voxels.length > size ** 3) return { error: 'too many blocks' };
  const cells = new Map<string, BlockId>();
  for (const v of voxels) {
    const [x, y, z, b] = v;
    if (![x, y, z].every((n) => Number.isInteger(n) && n >= 0 && n < size))
      return { error: 'outside the plot' };
    if (!palette.includes(b)) return { error: 'that block is not in this palette' };
    const k = cellKey(x, y, z);
    if (cells.has(k)) return { error: 'two blocks in one cell' };
    cells.set(k, b);
  }
  return cells;
}

function diff(want: Map<string, BlockId>, got: Map<string, BlockId>): Mismatch[] {
  const out: Mismatch[] = [];
  const at = (k: string) => k.split(',').map(Number) as [number, number, number];
  for (const [k, w] of want) {
    const g = got.get(k) ?? null;
    if (g !== w) {
      const [x, y, z] = at(k);
      out.push({ x, y, z, kind: g ? 'wrong' : 'missing', want: w, got: g });
    }
  }
  for (const [k, g] of got)
    if (!want.has(k)) {
      const [x, y, z] = at(k);
      out.push({ x, y, z, kind: 'extra', want: null, got: g });
    }
  // Bottom layer first, then reading order, so "next mismatch" walks up the build.
  return out.sort((a, b) => a.y - b.y || a.z - b.z || a.x - b.x);
}

/** Compares a build with the blueprint (and its turns, when the level accepts them). */
export function compare(
  level: BlueprintLevel,
  build: readonly Voxel[],
): CompareResult | { error: string } {
  const got = toCells(build, level.size, level.palette);
  if (got instanceof Map) {
    let best: CompareResult | null = null;
    for (const turns of level.rotations ? [0, 1, 2, 3] : [0]) {
      const want = toCells(
        level.model.map((v) => rotate(v, turns, level.size)),
        level.size,
      ) as Map<string, BlockId>;
      const mismatches = diff(want, got);
      if (!best || mismatches.length < best.mismatches.length)
        best = { match: !mismatches.length, rotation: turns as 0 | 1 | 2 | 3, mismatches };
    }
    return best!;
  }
  return got;
}

export const blueprintMedal = (wrongChecks: number) =>
  wrongChecks <= GOLD_MAX_WRONG_CHECKS ? ('gold' as const) : ('solved' as const);
